import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { avanzoApi } from "@/lib/avanzoApi";

export default function AvanzoProgress() {
  const [summary, setSummary] = useState(null);
  const [pcStatus, setPcStatus] = useState(null);
  const [pcResult, setPcResult] = useState(null);
  const [bands, setBands] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      avanzoApi.summary(),
      avanzoApi.progressStatus(),
      avanzoApi.paesProgressBands().catch(() => ({ data: null })),
    ])
      .then(async ([s, st, bR]) => {
        setSummary(s.data);
        setPcStatus(st.data);
        setBands(bR.data);
        if (st.data?.last_run_id) {
          try {
            const r = await avanzoApi.progressResult();
            setPcResult(r.data);
          } catch { /* no completed run yet */ }
        }
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="avz-empty" data-testid="avz-progress-loading">Cargando…</div>;
  if (!summary) return <div className="avz-empty">Estamos empezando a conocerte.</div>;

  const noEvidence = summary.attempts_total === 0;
  const g = pcStatus?.gate || {};
  const unlocked = !!g.unlocked;
  const hasActive = !!pcStatus?.has_active_run;

  return (
    <div data-testid="avz-progress">
      <section className="avz-card">
        <div className="avz-mute">Progreso general</div>
        <div className="avz-h1" data-testid="avz-progress-label">
          {noEvidence ? "Estamos empezando a conocerte" : summary.overall_progress_label}
        </div>
      </section>

      {/* Materias */}
      <section className="avz-grid avz-grid-2 avz-mt-4">
        {summary.subjects.map((s) => {
          const mastery = Math.max(0, Math.min(1, Number(s.avg_mastery || 0)));
          const pct = s.skills_practiced === 0 ? 4 : Math.max(6, Math.round(mastery * 100));
          return (
            <div
              className="avz-card avz-subject-card"
              key={s.key}
              data-subject={s.key}
              data-testid={`avz-progress-subject-${s.key}`}
              style={{ borderTop: `4px solid var(--avz-subject-${s.key})` }}
            >
              <div className="avz-row-between">
                <h3 className="avz-h3" style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    className="avz-subject-dot"
                    aria-hidden="true"
                    style={{ background: `var(--avz-subject-${s.key})` }}
                  />
                  {s.label}
                </h3>
                <span className="avz-chip">{s.mastery_label}</span>
              </div>
              <div
                className="avz-bar avz-mt-4"
                role="progressbar"
                aria-label={`Progreso en ${s.label}`}
                aria-valuenow={Math.round(mastery * 100)}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className="avz-bar-fill"
                  style={{
                    width: `${pct}%`,
                    background: `var(--avz-subject-${s.key})`,
                  }}
                />
              </div>
              <p className="avz-mute avz-mt-4">
                {s.skills_practiced} {s.skills_practiced === 1 ? "habilidad practicada" : "habilidades practicadas"}
              </p>
              <p className="avz-mute">
                {s.skills_strong.length > 0
                  ? `Buen dominio en: ${s.skills_strong.map(friendly).slice(0, 3).join(", ")}`
                  : "Aún no hay habilidades consolidadas."}
              </p>
              {s.skills_growing.length > 0 && (
                <p className="avz-mute">
                  Vamos a fortalecer: {s.skills_growing.map(friendly).slice(0, 3).join(", ")}
                </p>
              )}
            </div>
          );
        })}
      </section>

      {/* Control de progreso */}
      <section className="avz-card avz-mt-8" data-testid="avz-progress-check-card">
        <div className="avz-row-between">
          <div>
            <h3 className="avz-h3">Control de progreso</h3>
            <p className="avz-mute">Veamos cuánto has avanzado — 16 actividades, 4 por materia.</p>
          </div>
          <span className={`avz-chip ${unlocked ? "avz-chip-good" : ""}`}
                data-testid="avz-pc-status">
            {hasActive ? "En curso" : (unlocked ? "Disponible" : "Se habilita pronto")}
          </span>
        </div>

        {pcResult && !hasActive && (
          <div className="avz-mt-4" data-testid="avz-pc-last-result">
            <div className="avz-mute">Tu último control de progreso</div>
            <div className="avz-h2">{pcResult.band_label}</div>
            <div className="avz-small">
              {pcResult.total_correct} de {pcResult.total_items} — {pcResult.percentage}%
            </div>
          </div>
        )}

        {unlocked ? (
          <Link to="/avanzo/control-progreso"
                className="avz-btn avz-btn-primary avz-mt-4"
                data-testid="avz-pc-start">
            {hasActive
              ? "Continuar mi Control de progreso"
              : (pcResult ? "Ver mi resultado" : "Comenzar mi Control de progreso")}
          </Link>
        ) : (
          <p className="avz-lead avz-mt-4" data-testid="avz-pc-locked-copy">
            Se habilita cuando lleves 6 sesiones de entrenamiento en 3 materias.
            Llevas {g.sessions ?? 0} sesión(es) en {g.subjects_practiced ?? 0} materia(s).
          </p>
        )}
      </section>

      {/* Fase 5 · Bandas pedagógicas por habilidad */}
      {bands && (
        <section className="avz-mt-8" data-testid="avz-bands">
          <h3 className="avz-h2">Habilidades por dominio</h3>
          {(bands.solido.count + bands.desarrollo.count + bands.practica.count) === 0 ? (
            <div className="avz-card avz-mt-4" data-testid="avz-bands-empty">
              <p className="avz-mute">
                Aún no tenemos suficiente evidencia. Completa algunas sesiones de
                entrenamiento y aquí verás tus habilidades agrupadas.
              </p>
              <Link to="/avanzo/entrenar"
                    className="avz-btn avz-btn-primary avz-mt-4"
                    data-testid="avz-bands-empty-cta"
                    style={{ display: "inline-block", marginTop: 12 }}>
                Comenzar mi primera sesión
              </Link>
            </div>
          ) : (
            <>
              <BandCard title="Dominio sólido" tone="good" band={bands.solido} testid="avz-band-solid" />
              <BandCard title="En desarrollo" tone="mid" band={bands.desarrollo} testid="avz-band-developing" />
              <BandCard title="Necesita práctica" tone="warn" band={bands.practica} testid="avz-band-needs" />
            </>
          )}
        </section>
      )}
    </div>
  );
}

function BandCard({ title, tone, band, testid }) {
  const bg = { good: "#ecfdf5", mid: "#fef9c3", warn: "#fef2f2" }[tone];
  const border = { good: "#a7f3d0", mid: "#fde68a", warn: "#fecaca" }[tone];
  return (
    <div className="avz-card avz-mt-4" style={{ background: bg, borderColor: border }} data-testid={testid}>
      <div className="avz-row-between">
        <h4 className="avz-h3" style={{ margin: 0 }}>{title}</h4>
        <span className="avz-chip" data-testid={`${testid}-count`}>{band.count}</span>
      </div>
      {band.items.length === 0 ? (
        <p className="avz-mute avz-mt-4">Aún no hay habilidades en este grupo.</p>
      ) : (
        <ul className="avz-mt-4" style={{ listStyle: "none", padding: 0 }}>
          {band.items.slice(0, 5).map((it, i) => (
            <li key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0" }}
                data-testid={`${testid}-item-${i}`}>
              <span style={{ textTransform: "capitalize" }}>{friendly(it.habilidad)}</span>
              <Link
                to={`/avanzo/practica`}
                state={{ subject: it.materia, habilidad: it.habilidad }}
                className="avz-chip"
                data-testid={`${testid}-cta-${i}`}
              >
                Entrenar esta habilidad
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function friendly(raw) {
  return String(raw).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
